import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiCheckCircle,
  FiCreditCard,
  FiMinus,
  FiMessageSquare,
  FiPackage,
  FiPlus,
  FiTruck,
} from "react-icons/fi";
import { toast } from "react-toastify";

import { listProductsByShop } from "../../actions/productActions";
import {
  createServiceOrder,
  listServices,
  quoteServiceOrder,
} from "../../actions/serviceActions";
import { getPaymentConfig } from "../../actions/paymentActions";
import { HABERDASHERY_SHOP_KEY } from "../../constants/shopMeta";
import { PAYMENT_PROVIDERS } from "../../constants/paymentConstants";
import Loader from "../../components/Loader";
import Meta from "../../components/Meta";
import Message from "../../components/Message";
import { getSafeUserMessage } from "../../utils/errorMessages";
import {
  formatServiceMethod,
  getCustomerProductName,
  getIncludedFinishQuestion,
  getIncludedLabel,
  getPriceLabel,
  getQuantityQuestion,
  getQuantitySummary,
  getServiceKind,
  getServiceDisplayName,
  getServiceOptionDisplayName,
  getServiceOptionIncludedLabel,
} from "../../utils/serviceCustomerCopy";
import "./ServicesScreen.css";

const SERVICE_CATEGORIES = Object.freeze([
  {
    key: "buttonHole",
    route: "/services/button-holes",
    title: "Button Hole Service",
    description:
      "Choose your garment or buttonhole/eyelet finish.",
    chooserDescription:
      "Buttonholes and eyelets for tailoring, alterations and costume work.",
    chooserSupport:
      "Garment services include up to 10 buttonholes or eyelets.",
    chooserAction: "View buttonhole options",
    infoTitle: "Garment pricing",
    infoLines: [
      "Our garment prices include up to 10 buttonholes or eyelets on one garment.",
      "Need more than 10? Any additional buttonholes or eyelets are charged individually.",
    ],
  },
  {
    key: "coverButton",
    route: "/services/cover-buttons",
    title: "Cover Button Service",
    description:
      "Choose the button style and size you need.",
    chooserDescription: "Covered buttons in a range of styles and sizes.",
    chooserSupport:
      "Priced individually according to the button you choose.",
    chooserAction: "View cover button options",
    infoTitle: "",
    infoLines: [
      "Cover buttons are priced individually.",
      "Available button-back options can be chosen after selecting your button.",
    ],
  },
]);

const SERVICES_META = Object.freeze({
  chooser: {
    title: "In-House Costume Services | Academy Costumes London",
    description:
      "Book Academy Costumes London's in-house buttonhole, eyelet and cover button services for tailoring, alterations and costume work.",
    url: "/services",
  },
  buttonHole: {
    title: "Button Hole & Eyelet Service London | Academy Costumes London",
    description:
      "Professional buttonhole and eyelet services for tailoring, alterations and costume work at Academy Costumes London.",
    url: "/services/button-holes",
  },
  coverButton: {
    title: "Cover Button Service London | Academy Costumes London",
    description:
      "Covered button services in a range of styles and sizes for tailoring, costume and finishing work at Academy Costumes London.",
    url: "/services/cover-buttons",
  },
});

function getServiceKey(service) {
  return service?._id || service?.id || service?.slug || "";
}

function safeText(value = "") {
  return String(value ?? "").trim();
}

function formatMoney(value = 0, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: safeText(currency) || "GBP",
    }).format(Number(value || 0));
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
}

function getPricingOptions(service = {}) {
  const options = Array.isArray(service.pricingOptions)
    ? service.pricingOptions.filter((option) => option?.isActive !== false)
    : [];

  if (options.length) return options;

  return [
    {
      key: "standard",
      label: service.name || "Service",
      price: Number(service.basePrice || 0),
      unitLabel: service.unitLabel || "item",
      minQuantity: Number(service.minQuantity || 1),
      maxQuantity: Number(service.maxQuantity || 100),
      isDefault: true,
    },
  ];
}

function getDefaultOptionKey(service = {}) {
  const options = getPricingOptions(service);
  return (
    options.find((option) => option.isDefault)?.key ||
    options[0]?.key ||
    "standard"
  );
}

function getCheckoutUrl(payload = {}) {
  return (
    payload.checkoutUrl ||
    payload.hostedCheckoutUrl ||
    payload.approvalUrl ||
    payload.approveUrl ||
    payload.payerActionUrl ||
    payload.nextAction?.url ||
    ""
  );
}

function normalizeMatchText(value = "") {
  return safeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function getImageUrl(value = {}) {
  const direct = safeText(
    value.image || value.imageUrl || value.defaultImage || value.thumbnail,
  );
  if (direct) return direct;

  const images = Array.isArray(value.images) ? value.images : [];
  const first = images.find(Boolean);

  if (typeof first === "string") return safeText(first);

  return safeText(first?.url || first?.secure_url);
}

function getOptionCardKey(service = {}, option = {}) {
  return `${getServiceKey(service)}:${option.key || option.label || "standard"}`;
}

function getMatchKeys(value = {}, serviceKind = "") {
  const keys = [
    value.name,
    value.label,
    value.optionLabel,
    value.slug,
    getCustomerProductName(value, serviceKind ? { key: serviceKind } : null),
  ]
    .map(normalizeMatchText)
    .filter(Boolean);

  const expanded = new Set(keys);

  if (serviceKind === "buttonHole") {
    keys.forEach((key) => {
      const stripped = key.replace(/^(buttonholes|buttonhole|eyelets|eyelet)/, "");
      if (stripped) expanded.add(stripped);
    });
  }

  if (serviceKind === "coverButton") {
    keys.forEach((key) => {
      const stripped = key.replace(/coverbutton/g, "");
      if (stripped) expanded.add(stripped);
    });
  }

  return [...expanded];
}

function findMatchingCatalogueProduct(option = {}, service = {}, products = []) {
  const serviceKind = getServiceKind(service);
  const optionKeys = new Set(getMatchKeys(option, serviceKind));

  if (!serviceKind || !optionKeys.size) return null;

  return (
    products.find((product) => {
      if (getServiceKind(product) !== serviceKind) return false;
      return getMatchKeys(product, serviceKind).some((key) =>
        optionKeys.has(key),
      );
    }) || null
  );
}

function getOptionPriceLabel(option = {}, service = {}) {
  return getPriceLabel(
    {
      ...option,
      name: service.name,
      slug: service.slug,
    },
    service.currency,
  );
}

function getOptionInclusionLabel(option = {}, service = {}, product = null) {
  const productQuantity = Math.floor(Number(product?.includedQuantity || 0));

  if (productQuantity > 0) {
    return getIncludedLabel(productQuantity, product?.includedItemLabel);
  }

  return getServiceOptionIncludedLabel(option, service);
}

function isGarmentButtonHoleOption(service = {}, option = {}) {
  const unit = safeText(option.unitLabel || service.unitLabel)
    .toLowerCase()
    .replace(/^per\s+/, "");

  return getServiceKind(service) === "buttonHole" && unit === "garment";
}

function getIncludedFinishQuantity(option = {}, service = {}) {
  const included = Math.floor(
    Number(option.includedQuantity ?? service.includedQuantity ?? 0),
  );

  return Number.isFinite(included) && included > 0 ? included : 10;
}

function getInitialFinishQuantity(option = {}, service = {}) {
  return getIncludedFinishQuantity(option, service);
}

function getAdditionalFinishOptions(service = {}, selectedOption = {}) {
  if (getServiceKind(service) !== "buttonHole") return [];

  return getPricingOptions(service).filter(
    (option) =>
      option.key !== selectedOption?.key &&
      !isGarmentButtonHoleOption(service, option) &&
      Number(option.price || 0) > 0,
  );
}

function getBackOptions(value = {}) {
  const raw = Array.isArray(value.backOptions)
    ? value.backOptions
    : safeText(value.backOptions).split(/[,;\n\r]+/);

  return [...new Set(raw.map((item) => safeText(item)).filter(Boolean))];
}

function getSelectionBackOptions(option = {}, product = null) {
  return [
    ...new Set([
      ...getBackOptions(option),
      ...getBackOptions(product || {}),
      ...getBackOptions(product?.haberdashery || {}),
    ]),
  ];
}

function getRequiredFinishCountLabel(service = {}, option = {}, count = 0) {
  const finishLabel = getFinishItemLabel(service, option);
  return `${Math.max(0, Math.floor(Number(count || 0)))} ${finishLabel} per garment`;
}

function getFinishItemLabel(service = {}, option = {}) {
  const question = getIncludedFinishQuestion(service, option).toLowerCase();

  if (question.includes("buttonholes or eyelets")) {
    return "buttonholes or eyelets";
  }
  if (question.includes("eyelet")) return "eyelets";

  return "buttonholes";
}

function buildServiceMenuGroups(services = [], products = []) {
  return SERVICE_CATEGORIES.map((category) => {
    const categoryServices = services.filter(
      (candidate) => getServiceKind(candidate) === category.key,
    );

    return {
      ...category,
      services: categoryServices,
      items: categoryServices.flatMap((service) =>
        getPricingOptions(service).map((option) => {
          const product = findMatchingCatalogueProduct(option, service, products);

          return {
            key: getOptionCardKey(service, option),
            service,
            option,
            product,
            name: getServiceOptionDisplayName(option, service),
            priceLabel: getOptionPriceLabel(option, service),
            inclusionLabel: getOptionInclusionLabel(option, service, product),
            image: getImageUrl(option) || getImageUrl(product || {}),
          };
        }),
      ),
    };
  });
}

function clampQuantity(value, option = {}) {
  const min = Math.max(1, Math.floor(Number(option?.minQuantity || 1)));
  const max = Math.max(min, Math.floor(Number(option?.maxQuantity || 1000)));
  const next = Math.floor(Number(value));

  if (!Number.isFinite(next)) return min;

  return Math.min(max, Math.max(min, next));
}

function buildCustomerNotes(baseNotes = "", finishDetails = null) {
  const details = [];

  if (finishDetails?.enabled) {
    details.push(
      `Requested finishes per garment: ${finishDetails.quantity || 1}.`,
    );
  }

  if (safeText(finishDetails?.additionalFinish)) {
    details.push(`Additional finish: ${safeText(finishDetails.additionalFinish)}.`);
  }

  if (safeText(finishDetails?.buttonBack)) {
    details.push(`Button back: ${safeText(finishDetails.buttonBack)}.`);
  }

  if (safeText(baseNotes)) details.push(safeText(baseNotes));

  return details.join("\n\n");
}

function PaymentOption({ active, disabled, label, meta, onClick }) {
  return (
    <button
      type="button"
      className={`service-pay-option${active ? " service-pay-option--active" : ""}`}
      disabled={disabled}
      onClick={onClick}
    >
      <FiCreditCard aria-hidden="true" />
      <span>
        <strong>{label}</strong>
        <small>{meta}</small>
      </span>
    </button>
  );
}

export default function ServicesScreen({ serviceCategory = "" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const activeCategory =
    SERVICE_CATEGORIES.find((category) => category.key === serviceCategory) ||
    null;
  const isChooserScreen = !activeCategory;
  const currentServicesPath = activeCategory?.route || "/services";
  const meta = SERVICES_META[activeCategory?.key] || SERVICES_META.chooser;

  const { userInfo, isAuthenticated } = useSelector(
    (state) => state.userAuth || {},
  );
  const { loading, error, services = [] } = useSelector(
    (state) => state.serviceList,
  );
  const productList = useSelector((state) => state.productList || {});
  const quoteState = useSelector((state) => state.serviceOrderQuote || {});
  const createState = useSelector((state) => state.serviceOrderCreate || {});
  const paymentConfig = useSelector((state) => state.payment?.config || {});

  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [optionKey, setOptionKey] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [inboundMethod, setInboundMethod] = useState("drop_off");
  const [outboundMethod, setOutboundMethod] = useState("collect");
  const [selectedProvider, setSelectedProvider] = useState(PAYMENT_PROVIDERS.STRIPE);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [finishQuantity, setFinishQuantity] = useState(10);
  const [additionalFinishOptionKey, setAdditionalFinishOptionKey] = useState("");
  const [buttonBack, setButtonBack] = useState("");
  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    postalCode: "",
    country: "GB",
  });

  useEffect(() => {
    dispatch(listServices());
    dispatch(getPaymentConfig()).catch(() => {});
    dispatch(
      listProductsByShop(HABERDASHERY_SHOP_KEY, {
        pageNumber: 1,
        pageSize: 100,
        sort: "name",
        includeFacets: false,
      }),
    ).catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (!userInfo) return;
    setCustomerName((value) => value || safeText(userInfo.name));
    setCustomerPhone((value) => value || safeText(userInfo.phone));
  }, [userInfo]);

  const serviceMenuGroups = useMemo(
    () => buildServiceMenuGroups(services, productList.products || []),
    [productList.products, services],
  );

  const activeServiceMenuGroup = useMemo(
    () =>
      activeCategory
        ? serviceMenuGroups.find((group) => group.key === activeCategory.key) ||
          null
        : null,
    [activeCategory, serviceMenuGroups],
  );

  const serviceMenuItems = useMemo(
    () => activeServiceMenuGroup?.items || [],
    [activeServiceMenuGroup],
  );

  useEffect(() => {
    if (isChooserScreen) {
      if (selectedServiceId || optionKey) {
        setSelectedServiceId("");
        setOptionKey("");
      }
      return;
    }

    if (!selectedServiceId && !optionKey) return;

    const hasSelected = serviceMenuItems.some(
      (item) =>
        getServiceKey(item.service) === selectedServiceId &&
        item.option.key === optionKey,
    );

    if (!hasSelected) {
      setSelectedServiceId("");
      setOptionKey("");
      setAdditionalFinishOptionKey("");
      setButtonBack("");
    }
  }, [isChooserScreen, optionKey, selectedServiceId, serviceMenuItems]);

  const selectedService = useMemo(
    () =>
      services.find((service) => getServiceKey(service) === selectedServiceId) ||
      null,
    [services, selectedServiceId],
  );

  const pricingOptions = useMemo(
    () => getPricingOptions(selectedService || {}),
    [selectedService],
  );

  const selectedOption = useMemo(
    () =>
      pricingOptions.find((option) => option.key === optionKey) ||
      pricingOptions[0] ||
      null,
    [optionKey, pricingOptions],
  );

  const selectedMenuItem = useMemo(
    () =>
      serviceMenuItems.find(
        (item) =>
          getServiceKey(item.service) === selectedServiceId &&
          item.option.key === optionKey,
      ) || null,
    [optionKey, selectedServiceId, serviceMenuItems],
  );

  const selectedServiceName = useMemo(
    () => getServiceDisplayName(selectedService || {}),
    [selectedService],
  );

  const selectedOptionName = useMemo(
    () => getServiceOptionDisplayName(selectedOption || {}, selectedService || {}),
    [selectedOption, selectedService],
  );

  const quantityQuestionLabel = useMemo(
    () => getQuantityQuestion(selectedService || {}, selectedOption || {}),
    [selectedOption, selectedService],
  );

  const selectedQuantitySummary = useMemo(
    () => getQuantitySummary(quantity, selectedService || {}, selectedOption || {}),
    [quantity, selectedOption, selectedService],
  );

  const selectedPriceLabel = useMemo(
    () => getOptionPriceLabel(selectedOption || selectedService || {}, selectedService || {}),
    [selectedOption, selectedService],
  );

  const selectedInclusionLabel = useMemo(
    () =>
      selectedMenuItem?.inclusionLabel ||
      getServiceOptionIncludedLabel(selectedOption || {}, selectedService || {}),
    [selectedMenuItem, selectedOption, selectedService],
  );

  const showFinishQuantityField = isGarmentButtonHoleOption(
    selectedService || {},
    selectedOption || {},
  );

  const includedFinishQuantity = showFinishQuantityField
    ? getIncludedFinishQuantity(selectedOption || {}, selectedService || {})
    : 0;

  const additionalFinishCountPerGarment = showFinishQuantityField
    ? Math.max(0, Number(finishQuantity || 0) - includedFinishQuantity)
    : 0;

  const needsAdditionalFinish = additionalFinishCountPerGarment > 0;

  const additionalFinishOptions = useMemo(
    () => getAdditionalFinishOptions(selectedService || {}, selectedOption || {}),
    [selectedOption, selectedService],
  );

  const selectedAdditionalFinishOption = useMemo(
    () =>
      additionalFinishOptions.find(
        (option) => option.key === additionalFinishOptionKey,
      ) || null,
    [additionalFinishOptionKey, additionalFinishOptions],
  );

  const buttonBackOptions = useMemo(
    () =>
      getServiceKind(selectedService || {}) === "coverButton"
        ? getSelectionBackOptions(selectedOption || {}, selectedMenuItem?.product)
        : [],
    [selectedMenuItem, selectedOption, selectedService],
  );

  const finishQuantityLabel = useMemo(
    () => getIncludedFinishQuestion(selectedService || {}, selectedOption || {}),
    [selectedOption, selectedService],
  );

  const requiredFinishSummary = useMemo(
    () =>
      showFinishQuantityField
        ? getRequiredFinishCountLabel(
            selectedService || {},
            selectedOption || {},
            finishQuantity,
          )
        : "",
    [finishQuantity, selectedOption, selectedService, showFinishQuantityField],
  );

  const finishItemLabel = useMemo(
    () => getFinishItemLabel(selectedService || {}, selectedOption || {}),
    [selectedOption, selectedService],
  );

  const additionalFinishSummary = needsAdditionalFinish
    ? `${additionalFinishCountPerGarment} additional ${finishItemLabel} per garment`
    : "";
  const waitingForAdditionalFinish =
    needsAdditionalFinish && !additionalFinishOptionKey;

  useEffect(() => {
    if (!selectedService) return;
    const keys = pricingOptions.map((option) => option.key);
    if (!keys.includes(optionKey)) {
      setOptionKey(getDefaultOptionKey(selectedService));
    }
  }, [optionKey, pricingOptions, selectedService]);

  useEffect(() => {
    if (!needsAdditionalFinish) {
      setAdditionalFinishOptionKey("");
      return;
    }

    const hasSelected = additionalFinishOptions.some(
      (option) => option.key === additionalFinishOptionKey,
    );

    if (!hasSelected) {
      setAdditionalFinishOptionKey("");
    }
  }, [additionalFinishOptionKey, additionalFinishOptions, needsAdditionalFinish]);

  useEffect(() => {
    if (!buttonBackOptions.length) {
      setButtonBack("");
      return;
    }

    if (!buttonBackOptions.includes(buttonBack)) {
      setButtonBack(buttonBackOptions[0]);
    }
  }, [buttonBack, buttonBackOptions]);

  useEffect(() => {
    if (isChooserScreen) return undefined;
    if (!selectedServiceId || !optionKey) return undefined;
    if (needsAdditionalFinish && !additionalFinishOptionKey) return undefined;

    const timer = setTimeout(() => {
      dispatch(
        quoteServiceOrder({
          serviceId: selectedServiceId,
          optionKey,
          quantity,
          finishQuantity,
          additionalFinishOptionKey: needsAdditionalFinish
            ? additionalFinishOptionKey
            : "",
          buttonBack,
          inboundMethod,
          outboundMethod,
        }),
      ).catch(() => {});
    }, 250);

    return () => clearTimeout(timer);
  }, [
    dispatch,
    additionalFinishOptionKey,
    buttonBack,
    finishQuantity,
    inboundMethod,
    isChooserScreen,
    needsAdditionalFinish,
    optionKey,
    outboundMethod,
    quantity,
    selectedServiceId,
  ]);

  useEffect(() => {
    if (paymentConfig.sumupEnabled) {
      setSelectedProvider(PAYMENT_PROVIDERS.SUMUP);
    } else if (paymentConfig.paypalEnabled) {
      setSelectedProvider(PAYMENT_PROVIDERS.PAYPAL);
    } else if (paymentConfig.stripeEnabled) {
      setSelectedProvider(PAYMENT_PROVIDERS.STRIPE);
    }
  }, [
    paymentConfig.paypalEnabled,
    paymentConfig.stripeEnabled,
    paymentConfig.sumupEnabled,
  ]);

  const paymentOptions = useMemo(
    () =>
      [
        paymentConfig.sumupEnabled
          ? {
              provider: PAYMENT_PROVIDERS.SUMUP,
              label: "SumUp",
              meta: "Secure payment page",
            }
          : null,
        paymentConfig.paypalEnabled
          ? {
              provider: PAYMENT_PROVIDERS.PAYPAL,
              label: "PayPal",
              meta: "Secure checkout",
            }
          : null,
        paymentConfig.stripeEnabled
          ? {
              provider: PAYMENT_PROVIDERS.STRIPE,
              label: "Card",
              meta: "Secure card checkout",
            }
          : null,
      ].filter(Boolean),
    [
      paymentConfig.paypalEnabled,
      paymentConfig.stripeEnabled,
      paymentConfig.sumupEnabled,
    ],
  );

  const quote = quoteState.quote;
  const selectedProviderAvailable = paymentOptions.some(
    (option) => option.provider === selectedProvider,
  );
  const canSubmit = Boolean(
    selectedServiceId &&
      optionKey &&
      quote?.pricing?.grandTotal > 0 &&
      selectedProviderAvailable &&
      !waitingForAdditionalFinish &&
      !createState.loading,
  );
  const needsReturnAddress = outboundMethod === "return_shipping";

  const updateQuantity = (value) => {
    setQuantity(clampQuantity(value, selectedOption || {}));
  };

  const updateFinishQuantity = (value) => {
    const next = Math.floor(Number(value));
    setFinishQuantity(Number.isFinite(next) && next > 0 ? next : 1);
  };

  const selectServiceOption = (item) => {
    if (!item?.service || !item?.option) return;

    setSelectedServiceId(getServiceKey(item.service));
    setOptionKey(item.option.key);
    setQuantity(clampQuantity(item.option.minQuantity || 1, item.option));
    setFinishQuantity(getInitialFinishQuantity(item.option, item.service));
    setAdditionalFinishOptionKey("");
    setButtonBack("");
    window.setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const submitHandler = async (event) => {
    event.preventDefault();

    if (!isAuthenticated) {
      navigate("/login", { state: { from: { pathname: currentServicesPath } } });
      return;
    }

    try {
      const result = await dispatch(
        createServiceOrder({
          serviceId: selectedServiceId,
          optionKey,
          quantity,
          finishQuantity,
          additionalFinishOptionKey: needsAdditionalFinish
            ? additionalFinishOptionKey
            : "",
          buttonBack,
          inboundMethod,
          outboundMethod,
          provider: selectedProvider,
          customerName,
          customerPhone,
          customerNotes: buildCustomerNotes(customerNotes, {
            enabled: showFinishQuantityField,
            quantity: finishQuantity,
            additionalFinish: selectedAdditionalFinishOption
              ? getServiceOptionDisplayName(
                  selectedAdditionalFinishOption,
                  selectedService || {},
                )
              : "",
            buttonBack,
          }),
          shippingAddress: address,
        }),
      );

      const checkoutUrl = getCheckoutUrl(result);
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }

      const reference = result?.serviceOrder?.reference;
      if (reference) {
        navigate(`/services/orders/${encodeURIComponent(reference)}`);
      }
    } catch (err) {
      toast.error(
        getSafeUserMessage(
          err?.message,
          "We could not start this service order right now. Please try again later.",
        ),
      );
    }
  };

  if (isChooserScreen) {
    return (
      <main className="services-page">
        <Meta
          title={meta.title}
          description={meta.description}
          image="/images/home/academy-costumes.png"
          url={meta.url}
          breadcrumbs={[
            { name: "Home", url: "/" },
            { name: "Services", url: "/services" },
          ]}
        />

        <section className="services-hero">
          <img
            className="services-hero__image"
            src="/images/home/academy-costumes.png"
            alt="Academy Costumes London garments, trims and buttons"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />

          <div className="services-hero__overlay" />

          <div className="services-hero__content">
            <p className="services-hero__kicker">Academy Costumes London</p>
            <h1>In-House Services</h1>
            <p>
              Specialist finishing services for tailoring, costume and
              alterations.
            </p>
          </div>
        </section>

        <section className="services-flow services-flow--chooser">
          <div className="services-chooser">
            <header className="services-chooser__header">
              <span>In-House Services</span>
              <h2>Choose the service you need.</h2>
            </header>

            <div className="services-chooser__grid">
              {SERVICE_CATEGORIES.map((category) => (
                <Link
                  className="services-chooser-card"
                  key={category.key}
                  to={category.route}
                >
                  <span className="services-chooser-card__body">
                    <strong>{category.title}</strong>
                    <span>{category.chooserDescription}</span>
                    <small>{category.chooserSupport}</small>
                  </span>
                  <span className="services-chooser-card__action">
                    {category.chooserAction} &rarr;
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="services-page">
      <Meta
        title={meta.title}
        description={meta.description}
        image="/images/home/academy-costumes.png"
        url={meta.url}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Services", url: "/services" },
          { name: activeCategory.title, url: activeCategory.route },
        ]}
      />

      <section className="services-hero">
        <img
          className="services-hero__image"
          src="/images/home/academy-costumes.png"
          alt="Academy Costumes London garments, trims and buttons"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />

        <div className="services-hero__overlay" />

        <div className="services-hero__content">
          <p className="services-hero__kicker">Academy Costumes London</p>
          <h1>{activeCategory.title}</h1>
          <p>{activeCategory.description}</p>
        </div>
      </section>

      <section className="services-flow services-flow--detail">
        <div className="services-flow__catalog">
          <Link className="services-back-link" to="/services">
            &larr; Back to In-House Services
          </Link>

          <header className="services-detail-header">
            <h2>{activeCategory.title}</h2>
            <p>{activeCategory.description}</p>
            <div className="services-menu-column__info">
              {activeCategory.infoTitle ? (
                <strong>{activeCategory.infoTitle}</strong>
              ) : null}
              {activeCategory.infoLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </header>

          {loading ? (
            <div className="services-state">
              <Loader />
            </div>
          ) : error ? (
            <Message variant="danger">{error}</Message>
          ) : !serviceMenuItems.length ? (
            <Message>No services are available in this section yet.</Message>
          ) : (
            <div className="services-option-grid services-option-grid--detail">
              {serviceMenuItems.map((item) => {
                const isSelected = item.key === selectedMenuItem?.key;

                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`service-option-card${
                      isSelected ? " service-option-card--selected" : ""
                    }`}
                    aria-pressed={isSelected}
                    onClick={() => selectServiceOption(item)}
                  >
                    <span className="service-option-card__image">
                      {item.image ? (
                        <img src={item.image} alt="" loading="lazy" />
                      ) : (
                        <span
                          className="service-option-card__placeholder"
                          aria-hidden="true"
                        />
                      )}
                    </span>

                    <span className="service-option-card__body">
                      <strong>{item.name}</strong>
                      <span className="service-option-card__price">
                        {item.priceLabel}
                      </span>
                      {item.inclusionLabel ? (
                        <span className="service-option-card__included">
                          {item.inclusionLabel}
                        </span>
                      ) : null}
                    </span>

                    {isSelected ? (
                      <span className="service-option-card__selected">
                        <FiCheckCircle aria-hidden="true" />
                        Selected
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {selectedMenuItem ? (
        <aside id="service-order" className="services-booking" ref={formRef}>
          <div className="services-booking__header">
            <span>Your order</span>
            <h2>Your order</h2>
            <p>Choose your service and quantity to see your price.</p>
          </div>

          {quoteState.error ? (
            <div className="services-alert services-alert--error">
              {quoteState.error}
            </div>
          ) : null}

          {createState.error ? (
            <div className="services-alert services-alert--error">
              {createState.error}
            </div>
          ) : null}

          {!isAuthenticated ? (
            <div className="services-alert">
              <Link to="/login">Sign in</Link> to continue and track your
              order.
            </div>
          ) : null}

          <form className="services-form" onSubmit={submitHandler}>
            {selectedService ? (
              <div className="services-form__selected">
                <strong>{selectedOptionName || selectedServiceName}</strong>
                <span>
                  {selectedPriceLabel}
                </span>
                {selectedInclusionLabel ? (
                  <span>{selectedInclusionLabel}</span>
                ) : null}
                <span>{selectedQuantitySummary}</span>
              </div>
            ) : null}

            {buttonBackOptions.length ? (
              <fieldset className="services-choice-group">
                <legend>Choose your button back</legend>
                <div className="services-choice-group__options">
                  {buttonBackOptions.map((option) => (
                    <label key={option} className="services-radio-card">
                      <input
                        type="radio"
                        name="buttonBack"
                        value={option}
                        checked={buttonBack === option}
                        onChange={(event) => setButtonBack(event.target.value)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : null}

            <div className="services-form__grid">
              <label className="services-form__field" htmlFor="quantity">
                <span>{quantityQuestionLabel}</span>
                <div className="services-quantity-stepper">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    disabled={
                      Number(quantity) <= Number(selectedOption?.minQuantity || 1)
                    }
                    onClick={() => updateQuantity(Number(quantity) - 1)}
                  >
                    <FiMinus aria-hidden="true" />
                  </button>
                  <input
                    id="quantity"
                    type="number"
                    min={selectedOption?.minQuantity || 1}
                    max={selectedOption?.maxQuantity || 1000}
                    value={quantity}
                    onChange={(event) => updateQuantity(event.target.value)}
                    required
                  />
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    disabled={
                      Number(quantity) >= Number(selectedOption?.maxQuantity || 1000)
                    }
                    onClick={() => updateQuantity(Number(quantity) + 1)}
                  >
                    <FiPlus aria-hidden="true" />
                  </button>
                </div>
              </label>

              {showFinishQuantityField ? (
                <label className="services-form__field" htmlFor="finish-quantity">
                  <span>{finishQuantityLabel}</span>
                  <input
                    id="finish-quantity"
                    type="number"
                    min="1"
                    value={finishQuantity}
                    onChange={(event) => updateFinishQuantity(event.target.value)}
                  />
                </label>
              ) : null}
            </div>

            {showFinishQuantityField ? (
              <div className="services-finish-summary">
                <span>{requiredFinishSummary}</span>
                {needsAdditionalFinish ? (
                  <>
                    <strong>{additionalFinishSummary}</strong>
                    <small>
                      Choose the finish below so we can price the additional
                      work.
                    </small>
                  </>
                ) : (
                  <strong>Included in your garment price</strong>
                )}
              </div>
            ) : null}

            {needsAdditionalFinish && additionalFinishOptions.length ? (
              <fieldset className="services-choice-group">
                <legend>Choose the finish for your additional {finishItemLabel}</legend>
                <div className="services-choice-group__options">
                  {additionalFinishOptions.map((option) => (
                    <label key={option.key} className="services-radio-card">
                      <input
                        type="radio"
                        name="additionalFinish"
                        value={option.key}
                        checked={additionalFinishOptionKey === option.key}
                        onChange={(event) =>
                          setAdditionalFinishOptionKey(event.target.value)
                        }
                      />
                      <span>
                        <strong>
                          {getServiceOptionDisplayName(
                            option,
                            selectedService || {},
                          )}
                        </strong>
                        <small>
                          {getOptionPriceLabel(option, selectedService || {})}
                        </small>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : needsAdditionalFinish ? (
              <p className="services-form__note">
                Please tell us which additional finish you need in the notes and
                we will confirm the extra charge before completing the work.
              </p>
            ) : null}

            <div className="services-form__grid">
              <label className="services-form__field" htmlFor="inbound">
                <span>
                  <FiPackage aria-hidden="true" />
                  Getting your garment to us
                </span>
                <select
                  id="inbound"
                  value={inboundMethod}
                  onChange={(event) => setInboundMethod(event.target.value)}
                >
                  <option value="drop_off">
                    {formatServiceMethod("drop_off")}
                  </option>
                  <option value="ship_to_store">
                    {formatServiceMethod("ship_to_store")}
                  </option>
                </select>
              </label>

              <label className="services-form__field" htmlFor="outbound">
                <span>
                  <FiTruck aria-hidden="true" />
                  Getting your garment back
                </span>
                <select
                  id="outbound"
                  value={outboundMethod}
                  onChange={(event) => setOutboundMethod(event.target.value)}
                >
                  <option value="collect">{formatServiceMethod("collect")}</option>
                  <option value="return_shipping">
                    {formatServiceMethod("return_shipping")}
                  </option>
                </select>
              </label>
            </div>

            {needsReturnAddress ? (
              <div className="services-address">
                <label className="services-form__field" htmlFor="line1">
                  <span>Address line 1</span>
                  <input
                    id="line1"
                    value={address.line1}
                    onChange={(event) =>
                      setAddress((current) => ({
                        ...current,
                        line1: event.target.value,
                      }))
                    }
                    required
                  />
                </label>
                <label className="services-form__field" htmlFor="line2">
                  <span>Address line 2</span>
                  <input
                    id="line2"
                    value={address.line2}
                    onChange={(event) =>
                      setAddress((current) => ({
                        ...current,
                        line2: event.target.value,
                      }))
                    }
                  />
                </label>
                <div className="services-form__grid">
                  <label className="services-form__field" htmlFor="city">
                    <span>City</span>
                    <input
                      id="city"
                      value={address.city}
                      onChange={(event) =>
                        setAddress((current) => ({
                          ...current,
                          city: event.target.value,
                        }))
                      }
                      required
                    />
                  </label>
                  <label className="services-form__field" htmlFor="postcode">
                    <span>Postcode</span>
                    <input
                      id="postcode"
                      value={address.postalCode}
                      onChange={(event) =>
                        setAddress((current) => ({
                          ...current,
                          postalCode: event.target.value,
                        }))
                      }
                      required
                    />
                  </label>
                </div>
              </div>
            ) : null}

            <div className="services-form__grid">
              <label className="services-form__field" htmlFor="name">
                <span>Name</span>
                <input
                  id="name"
                  type="text"
                  autoComplete="name"
                  value={customerName}
                  onChange={(event) => setCustomerName(event.target.value)}
                  required
                />
              </label>

              <label className="services-form__field" htmlFor="phone">
                <span>Phone</span>
                <input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  value={customerPhone}
                  onChange={(event) => setCustomerPhone(event.target.value)}
                />
              </label>
            </div>

            <label className="services-form__field" htmlFor="message">
              <span>
                <FiMessageSquare aria-hidden="true" />
                Notes for us
              </span>
              <textarea
                id="message"
                rows="4"
                value={customerNotes}
                onChange={(event) => setCustomerNotes(event.target.value)}
                placeholder="Tell us about the garment, fabric, deadline or special handling."
              />
            </label>

            <div className="services-quote">
              {quoteState.loading ? (
                <span>Updating your price...</span>
              ) : waitingForAdditionalFinish ? (
                <span>Choose the finish for your additional {finishItemLabel}.</span>
              ) : quote ? (
                <>
                  <div>
                    <span>
                      {showFinishQuantityField
                        ? "Garment service"
                        : Number(quantity) > 1 && selectedOption
                          ? `${Number(quantity)} × ${formatMoney(
                              selectedOption.price,
                              selectedService?.currency,
                            )}`
                          : selectedOptionName || "Your selection"}
                    </span>
                    <strong>
                      {formatMoney(
                        showFinishQuantityField
                          ? quote.pricing.serviceTotal || quote.pricing.subtotal
                          : quote.pricing.subtotal,
                        quote.pricing.currency,
                      )}
                    </strong>
                  </div>
                  {showFinishQuantityField ? (
                    <div>
                      <span>
                        {includedFinishQuantity} {finishItemLabel} included per
                        garment
                      </span>
                      <strong>Included</strong>
                    </div>
                  ) : null}
                  {showFinishQuantityField &&
                  quote.serviceSnapshot?.additionalFinishQuantity > 0 ? (
                    <div>
                      <span>
                        {quote.serviceSnapshot.additionalFinishQuantity} additional{" "}
                        {finishItemLabel} ×{" "}
                        {formatMoney(
                          quote.serviceSnapshot.additionalFinishUnitPrice,
                          quote.pricing.currency,
                        )}
                      </span>
                      <strong>
                        {formatMoney(
                          quote.pricing.additionalFinishTotal,
                          quote.pricing.currency,
                        )}
                      </strong>
                    </div>
                  ) : null}
                  <div>
                    <span>Post back to you</span>
                    <strong>
                      {formatMoney(
                        quote.pricing.outboundShippingTotal,
                        quote.pricing.currency,
                      )}
                    </strong>
                  </div>
                  <div className="services-quote__total">
                    <span>Your price</span>
                    <strong>
                      {formatMoney(
                        quote.pricing.grandTotal,
                        quote.pricing.currency,
                      )}
                    </strong>
                  </div>
                </>
              ) : (
                <span>Choose your service and we&apos;ll show you the price.</span>
              )}
            </div>

            <div className="service-pay-options">
              {paymentOptions.map((option) => (
                <PaymentOption
                  key={option.provider}
                  active={selectedProvider === option.provider}
                  disabled={createState.loading}
                  label={option.label}
                  meta={option.meta}
                  onClick={() => setSelectedProvider(option.provider)}
                />
              ))}
            </div>

            <button
              type="submit"
              className="services-form__submit"
              disabled={!canSubmit}
            >
              {createState.loading
                ? "Opening secure payment..."
                : isAuthenticated
                  ? "Continue to secure payment"
                  : "Sign in to continue"}
            </button>
          </form>
        </aside>
        ) : null}
      </section>
    </main>
  );
}
